import React, { useState, useEffect } from 'react';
import { CHAINS, getPopularChains, getPopularTokens, isChainEnabledForNetwork, isTokenEnabledForNetwork, toConfidentialSymbol, toConfidentialName } from '../config/chains';

const TokenSelector = ({
    isOpen,
    onClose,
    onTokenSelect,
    onChainSelect,
    selectedChainId = null,
    selectedTokenId = null,
    title = "Select token to swap",
    showConfidential = false,
    networkMode = 'testnet',
    onNetworkModeChange = () => {},
}) => {
    const [chainSearchTerm, setChainSearchTerm] = useState('');
    const [tokenSearchTerm, setTokenSearchTerm] = useState('');
    const [selectedChain, setSelectedChain] = useState(selectedChainId);
    const [selectedToken, setSelectedToken] = useState(selectedTokenId);

    // Get popular chains and tokens
    const popularChains = getPopularChains(networkMode);
    const popularTokens = getPopularTokens();

    // Filter chains based on search term
    const filteredChains = popularChains.filter(chain =>
        chain.name.toLowerCase().includes(chainSearchTerm.toLowerCase())
    );

    // Helper function to check if a token address is valid
    const isValidTokenAddress = (address) => {
        if (!address) return false;
        const addr = address.trim().toLowerCase();
        // Check if it's empty or zero address
        if (addr === '' || addr === '0x0000000000000000000000000000000000000000') return false;
        // Check if it's a valid Ethereum address format
        return /^0x[a-fA-F0-9]{40}$/.test(addr);
    };

    // USDC/USDT/USDG/USDe where chains.js has a real address; CUSD only where
    // marked active (Arb/Eth mainnet today).
    const isTokenEnabled = (chainId, symbol) =>
        isTokenEnabledForNetwork(chainId, symbol, networkMode);

    // Get tokens to display - only show tokens when a chain is selected
    // This prevents duplicate tokens from appearing (e.g., USDC from multiple chains)
    const tokensToDisplay = selectedChain && CHAINS[selectedChain]
        ? Object.entries(CHAINS[selectedChain].tokens)
            .filter(([tokenId, token]) => {
                // Show tokens with valid addresses, plus explicitly inactive
                // "coming soon" placeholders (e.g. CUSD) that use a zero address.
                // Only on chains enabled for the current network mode.
                return isChainEnabledForNetwork(selectedChain, networkMode) &&
                    (isValidTokenAddress(token.address) || token.active === false);
            })
            .map(([tokenId, token]) => ({
                ...token,
                // Keep the bare token key (e.g. "usdc") separate from the
                // composite id. Chain keys like `arbitrum_mainnet` / `bnb_mainnet` contain
                // underscores, so `id.split('_')[1]` is NOT the token id.
                tokenId,
                id: `${selectedChain}_${tokenId}`,
                chainId: selectedChain,
                chainName: CHAINS[selectedChain].name,
                chainIcon: CHAINS[selectedChain].icon,
                chainColor: CHAINS[selectedChain].color,
                chainActive: isChainEnabledForNetwork(selectedChain, networkMode),
                tokenActive: isTokenEnabled(selectedChain, token.symbol)
            }))
        : []; // Show empty list when no chain is selected to prevent duplicates

    // Filter tokens based on search term and selected chain
    const filteredTokens = tokensToDisplay.filter(token => {
        const matchesSearch = token.symbol.toLowerCase().includes(tokenSearchTerm.toLowerCase()) ||
                             token.name.toLowerCase().includes(tokenSearchTerm.toLowerCase());
        const matchesChain = selectedChain ? token.chainId === selectedChain : true;
        return matchesSearch && matchesChain;
    });

    // Handle chain selection - just select the chain, don't auto-select token or close
    const handleChainSelect = (chainId) => {
        if (isChainEnabledForNetwork(chainId, networkMode)) {
            setSelectedChain(chainId);
            setSelectedToken(null); // Clear token selection when chain changes
            
            // Notify parent about chain change
            if (onChainSelect) {
                onChainSelect(chainId);
            }
            // Don't close modal - wait for user to select a token
        }
    };

    // Handle token selection - select token and close modal
    const handleTokenSelect = (tokenId) => {
        if (!selectedChain) {
            // Can't select token without chain
            return;
        }
        const tokenData = CHAINS[selectedChain].tokens[tokenId];
        if (!tokenData) {
            return;
        }
        if (!isTokenEnabled(selectedChain, tokenData.symbol)) {
            // Coming soon - not selectable yet
            return;
        }
        setSelectedToken(tokenId);
        const fullTokenData = {
            ...tokenData,
            tokenId,
            chainId: selectedChain,
            chainName: CHAINS[selectedChain].name,
            id: `${selectedChain}_${tokenId}`,
            chainLogo: CHAINS[selectedChain].icon,
            chainColor: CHAINS[selectedChain].color
        };
        
        // Notify parent about token selection
        if (onTokenSelect) {
            onTokenSelect(fullTokenData);
        }

        // Close modal after token is selected. This used to be deferred via
        // setTimeout(..., 100), but a timer is an unreliable way to drive UI
        // state — under any main-thread contention the close can be delayed
        // far past 100ms, during which the modal (and its still-open two-
        // column layout) stays visible, showing through the overlay's
        // translucent backdrop over the page behind it. Closing synchronously
        // removes that failure mode entirely.
        if (onClose) {
            onClose();
        }
    };

    // Sync selectedChain and selectedToken with props when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedChain(selectedChainId);
            setSelectedToken(selectedTokenId);
            setChainSearchTerm('');
            setTokenSearchTerm('');
        }
    }, [isOpen, selectedChainId, selectedTokenId]);

    if (!isOpen) return null;

    return (
        <div className="token-selector-overlay" onClick={onClose}>
            <div className="token-selector-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="token-selector-header">
                    <h2 className="token-selector-title">{title}</h2>
                    <button
                        className="token-selector-close"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>

                {/* Testnet/Mainnet toggle — governs both the chain list and,
                    through selectedChain, the token list below it. A
                    compact segmented control (one bordered track, sized to
                    its own content) rather than two buttons stretched
                    edge-to-edge — this is a binary switch, not two equally-
                    weighted primary actions, and every other pill control
                    in this app (Connect, the CTA, etc.) is content-sized
                    the same way. Inline styles (not a new stylesheet rule):
                    this component isn't part of the mobile handoff shell,
                    but keeping the same "style where it's used" approach
                    avoids adding another CSS surface to track. */}
                <div
                    style={{
                        padding: '18px 20px 16px',
                    }}
                >
                    <div
                        style={{
                            display: 'inline-flex',
                            border: '1px solid #E6E6E1',
                            borderRadius: 999,
                            padding: 3,
                            gap: 2,
                        }}
                    >
                        {['mainnet', 'testnet'].map((mode) => {
                            const active = networkMode === mode;
                            return (
                                <button
                                    key={mode}
                                    type="button"
                                    onClick={() => onNetworkModeChange(mode)}
                                    style={{
                                        padding: '7px 18px',
                                        border: 0,
                                        borderRadius: 999,
                                        background: active ? '#16181A' : 'transparent',
                                        color: active ? '#FFFFFF' : '#8E8E88',
                                        fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
                                        fontSize: 11,
                                        letterSpacing: '0.1em',
                                        cursor: 'pointer',
                                        transition: 'background .15s, color .15s',
                                    }}
                                >
                                    {mode === 'testnet' ? 'Testnet' : 'Mainnet'}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Side by Side Content - Chains on LEFT, Tokens on RIGHT */}
                <div className="token-selector-content-wrapper">
                    {/* Chain Selection - Left Side */}
                    <div className="chain-selection-panel">
                        <div className="panel-header">
                            <span>Chains ({networkMode})</span>
                        </div>

                        {/* Chain Search Bar */}
                        <div className="panel-search">
                            <i className="fas fa-search"></i>
                            <input
                                type="text"
                                placeholder="Search chains"
                                value={chainSearchTerm}
                                onChange={(e) => setChainSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Chain List */}
                        <div className="panel-content">
                            <div className="chain-list">
                                {filteredChains.map(chain => {
                                    const chainEnabled = isChainEnabledForNetwork(chain.id, networkMode);
                                    return (
                                    <div
                                        key={chain.id}
                                        className={`chain-option ${!chainEnabled ? 'disabled' : ''} ${selectedChain === chain.id ? 'selected' : ''}`}
                                        onClick={() => handleChainSelect(chain.id)}
                                    >
                                        <div className="chain-icon" style={{ color: chain.color }}>
                                            {chain.icon && chain.icon.startsWith('/') ? (
                                                <img 
                                                    src={chain.icon} 
                                                    alt={chain.name} 
                                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                    onError={(e) => {
                                                        // Fallback to colored circle with first letter if image fails to load
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div 
                                                style={{ 
                                                    display: chain.icon && chain.icon.startsWith('/') ? 'none' : 'flex',
                                                    width: '100%', 
                                                    height: '100%', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    fontSize: '1.2em',
                                                    fontWeight: 'bold',
                                                    color: 'white',
                                                    backgroundColor: chain.color,
                                                    borderRadius: '50%'
                                                }}
                                            >
                                                {chain.icon && !chain.icon.startsWith('/') ? chain.icon : chain.name.charAt(0)}
                                            </div>
                                        </div>
                                        <div className="chain-info">
                                            <span className="chain-name">{chain.name}</span>
                                            {!chainEnabled && (
                                                <span className="coming-soon">Coming Soon</span>
                                            )}
                                        </div>
                                        {selectedChain === chain.id && (
                                            <div className="selected-indicator">
                                                <i className="fas fa-check"></i>
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Token Selection - Right Side */}
                    <div className="token-selection-panel">
                        <div className="panel-header">
                            <span>Assets</span>
                        </div>
                        
                        {/* Token Search Bar */}
                        <div className="panel-search">
                            <i className="fas fa-search"></i>
                            <input
                                type="text"
                                placeholder="Search assets"
                                value={tokenSearchTerm}
                                onChange={(e) => setTokenSearchTerm(e.target.value)}
                                disabled={!selectedChain}
                            />
                        </div>

                        {/* Token List */}
                        <div className="panel-content">
                            <div className="token-list">
                                {filteredTokens.map(token => {
                                    const tokenId = token.tokenId;
                                    const isSelected = selectedToken === tokenId;
                                    const isEnabled = isTokenEnabled(token.chainId, token.symbol);
                                    return (
                                    <div
                                        key={token.id}
                                        className={`token-option ${!isEnabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
                                        onClick={() => handleTokenSelect(tokenId)}
                                    >
                                        <div className="token-icon" style={{ color: token.color }}>
                                            {token.icon.startsWith('/') ? (
                                                <img src={token.icon} alt={token.symbol} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                            ) : (
                                                <span style={{ fontSize: '1.25rem' }}>{token.icon}</span>
                                            )}
                                        </div>
                                        <div className="token-info">
                                            <div className="token-main">
                                                <span className="token-symbol">{showConfidential ? toConfidentialSymbol(token.symbol, token.name) : token.symbol}</span>
                                                <span className="token-name">{showConfidential ? toConfidentialName(token.symbol, token.name) : token.name}</span>
                                            </div>
                                            {!isEnabled && (
                                                <span className="coming-soon">Coming Soon</span>
                                            )}
                                        </div>
                                        {isSelected && (
                                            <div className="selected-indicator">
                                                <i className="fas fa-check"></i>
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>

                            {/* No tokens found or no chain selected */}
                            {filteredTokens.length === 0 && (
                                <div className="no-results">
                                    <i className="fas fa-search"></i>
                                    <p>{!selectedChain ? 'Please select a chain first' : 'No tokens found'}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TokenSelector;
